import { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../../contexts/LanguageContext";
import { initialsFrom } from "../../../utils/initials";
import { formatNumber, formatRelative } from "../format";
import { EmptyNote, Skeleton } from "./ui";

const FILTERS = ["all", "earned", "rewards", "expired"];

// Transaction kinds with copy of their own. A kind the API adds later falls
// back to the generic earned/removed line instead of a raw key.
const KNOWN_KINDS = new Set([
  "scan",
  "redeem",
  "initial",
  "manual",
  "birthday",
  "referrer",
  "referee",
  "shopify",
  "bookingkit",
  "expired",
  "upgrade",
]);

const matchesFilter = (item, filter) => {
  if (filter === "earned") return item.kind !== "expired" && Number(item.point) > 0;
  if (filter === "rewards") return item.kind === "redeem";
  if (filter === "expired") return item.kind === "expired";
  return true;
};

function actionKey(item) {
  const negative = Number(item.point) < 0;
  if (!KNOWN_KINDS.has(item.kind)) {
    return negative ? "dashboard.activity.kinds.otherNegative" : "dashboard.activity.kinds.other";
  }
  if ((item.kind === "manual" || item.kind === "bookingkit") && negative) {
    return `dashboard.activity.kinds.${item.kind}Negative`;
  }
  return `dashboard.activity.kinds.${item.kind}`;
}

const AVATAR_TONES = [
  "bg-indigo-50 text-indigo-800",
  "bg-orange-50 text-orange-800",
  "bg-emerald-50 text-emerald-800",
  "bg-gray-100 text-gray-700",
];

export default function ActivityFeed({ items, loading, className = "" }) {
  const { t, language } = useLanguage();
  const [filter, setFilter] = useState("all");
  const now = new Date();
  const visible = (items || []).filter((item) => matchesFilter(item, filter));

  return (
    <section
      aria-label={t("dashboard.activity.title")}
      className={`min-w-0 overflow-hidden rounded-[14px] border border-gray-200 bg-white ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4 sm:px-6">
        <h2 className="text-base font-semibold text-gray-900">
          {t("dashboard.activity.title")}
        </h2>
        <div
          role="group"
          aria-label={t("dashboard.activity.filterLabel")}
          className="flex flex-wrap gap-1"
        >
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
              className={`min-h-9 rounded-full border px-3 text-[13px] font-medium ${
                filter === f
                  ? "border-gray-900 bg-gray-900 text-white"
                  : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
              }`}
            >
              {t(`dashboard.activity.filters.${f}`)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <ul className="flex flex-col">
          {[0, 1, 2, 3, 4].map((i) => (
            <li key={i} className="flex items-center gap-3.5 border-b border-gray-50 px-5 py-3.5 sm:px-6">
              <Skeleton className="h-9 w-9 rounded-full" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-3.5 w-2/3" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </li>
          ))}
        </ul>
      ) : visible.length === 0 ? (
        <div className="p-5 sm:px-6">
          <EmptyNote>{t("dashboard.activity.empty")}</EmptyNote>
        </div>
      ) : (
        <ul className="flex flex-col">
          {visible.map((item, index) => {
            const point = Number(item.point) || 0;
            const name = item.customer_name || t("dashboard.activity.unknownCustomer");
            const meta = [
              item.staff_name &&
                t("dashboard.activity.scannedBy", { name: item.staff_name }),
              item.pass_name,
            ]
              .filter(Boolean)
              .join(" · ");
            const pillTone =
              item.kind === "expired"
                ? "bg-gray-100 text-gray-600"
                : point >= 0
                  ? "bg-indigo-50 text-indigo-800"
                  : "bg-orange-50 text-orange-800";
            return (
              <li
                key={item.id ?? index}
                className="flex items-center gap-3 border-b border-gray-50 px-5 py-3.5 last:border-b-0 sm:gap-3.5 sm:px-6"
              >
                <div
                  aria-hidden="true"
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold ${
                    AVATAR_TONES[(Number(item.customer_id) || index) % AVATAR_TONES.length]
                  }`}
                >
                  {initialsFrom(name) || "?"}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-sm text-gray-900">
                    {item.customer_id ? (
                      <Link
                        to={`/customers/${encodeURIComponent(item.customer_id)}`}
                        className="font-semibold text-gray-900 hover:text-indigo-700"
                      >
                        {name}
                      </Link>
                    ) : (
                      <span className="font-semibold">{name}</span>
                    )}{" "}
                    {t(actionKey(item), {
                      point: formatNumber(Math.abs(point), language),
                    })}
                  </span>
                  {meta && <span className="truncate text-xs text-gray-500">{meta}</span>}
                </div>
                {point !== 0 && (
                  <span
                    className={`hidden shrink-0 rounded-full px-2 py-0.5 text-[13px] font-semibold whitespace-nowrap sm:inline ${pillTone}`}
                  >
                    {point > 0 ? "+" : "−"}
                    {formatNumber(Math.abs(point), language)}
                  </span>
                )}
                <time
                  dateTime={item.time}
                  className="w-20 shrink-0 text-right text-xs text-gray-500"
                >
                  {formatRelative(item.time, language, now)}
                </time>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
